// Arquivo: src/components/TreeTable.tsx  (colar o conteúdo INTEGRAL no Code Mode)
// Matriz hierárquica EAP/WBS com drill-down (4 níveis), busca, expandir/recolher,
// ordenação natural por código (2.2.1.1 -> 2.2.1.10) e seleção de nó.
// Regra: TODOS os nós são renderizados, mesmo zerados — unidade nunca é omitida.

import { useMemo, useState } from 'react'
import type { TreeNode } from '@/domain/engineering'
import { compareNatural } from '@/domain/engineering'
import { moneyFmt, pctFmt } from '@/lib/format'

export interface TreeColumn {
  key: string
  label: string
  align?: 'left' | 'right' | 'center'
  kind?: 'money' | 'pct' | 'signed-pct' | 'text'
  positiveWhen?: (node: TreeNode) => number
}

interface TreeTableProps {
  nodes: TreeNode[]
  columns: TreeColumn[]
  searchPlaceholder: string
  infoText?: string
  selectedId?: string | null
  onSelect?: (node: TreeNode) => void
  emptyMessage?: string
  maxHeight?: number
}

function sortNodes(nodes: TreeNode[]): TreeNode[] {
  return [...nodes].sort((a, b) => compareNatural(a.codigo, b.codigo))
    .map(node => ({ ...node, children: sortNodes(node.children ?? []) }))
}

function nodeMatches(node: TreeNode, term: string): boolean {
  const t = term.toLowerCase()
  return node.codigo.toLowerCase().includes(t)
    || node.nome.toLowerCase().includes(t)
    || node.descricao.toLowerCase().includes(t)
}

function filterTree(nodes: TreeNode[], term: string): { visible: TreeNode[]; matched: number } {
  let matched = 0
  const walk = (list: TreeNode[]): TreeNode[] => {
    const result: TreeNode[] = []
    for (const node of list) {
      const children = walk(node.children ?? [])
      const self = nodeMatches(node, term)
      if (self) matched += 1
      if (self || children.length) result.push({ ...node, children })
    }
    return result
  }
  return { visible: walk(nodes), matched }
}

function collectIds(nodes: TreeNode[], into: Set<string> = new Set()): Set<string> {
  for (const node of nodes) {
    into.add(node.id)
    if (node.children?.length) collectIds(node.children, into)
  }
  return into
}

export function TreeTable({ nodes, columns, searchPlaceholder, infoText, selectedId, onSelect, emptyMessage = 'Nenhum item encontrado na estrutura EAP.', maxHeight = 520 }: TreeTableProps) {
  const ordered = useMemo(() => sortNodes(nodes), [nodes])
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const initial = new Set<string>()
    for (const level1 of nodes) {
      initial.add(level1.id)
      for (const level2 of level1.children ?? []) initial.add(level2.id)
    }
    return initial
  })
  const [internalSelected, setInternalSelected] = useState<string | null>(null)
  const currentSelected = selectedId !== undefined ? selectedId : internalSelected

  const { visible, matched } = useMemo(() => {
    if (!search.trim()) return { visible: ordered, matched: 0 }
    return filterTree(ordered, search.trim())
  }, [ordered, search])

  const searching = Boolean(search.trim())
  const expandedView = searching ? collectIds(visible) : expanded

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const renderNode = (node: TreeNode): React.ReactNode => {
    const hasChildren = (node.children?.length ?? 0) > 0
    const isOpen = expandedView.has(node.id)
    const selected = currentSelected === node.id
    const padClass = `cell-nivel-${Math.min(Math.max(node.nivel, 1), 4)}`
    return (
      <>
        <tr
          key={node.id}
          className={`tree-row ${selected ? 'selected' : ''}`}
          onClick={() => {
            setInternalSelected(node.id)
            if (hasChildren) toggle(node.id)
            onSelect?.(node)
          }}
        >
          <td className={padClass}>
            <span className={`tree-icon ${hasChildren ? (isOpen ? 'open' : 'closed') : 'leaf'}`}>{hasChildren ? '▼' : '•'}</span>
            <span className="tree-code">{node.codigo || '—'}</span>
            <span className="tree-name" title={node.descricao}>{node.nome}</span>
          </td>
          {columns.map(column => {
            const raw = (node as unknown as Record<string, unknown>)[column.key]
            const align = column.align ?? 'right'
            const cls = align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : ''
            let content: React.ReactNode = '—'
            let colorClass = ''
            if (column.kind === 'money') {
              content = moneyFmt(raw as string)
            }
            else if (column.kind === 'pct') {
              const n = typeof raw === 'number' ? raw : null
              content = n === null ? '—' : pctFmt(n > 1 ? n : n * 100)
            }
            else if (column.kind === 'signed-pct') {
              const n = typeof raw === 'number' ? raw : null
              content = n === null ? '—' : `${n > 0 ? '+' : ''}${pctFmt(n)}`
              if (n !== null) colorClass = n < 0 ? 'num-negative' : n > 0 ? 'num-positive' : ''
            }
            else {
              content = raw === null || raw === undefined || raw === '' ? '—' : String(raw)
            }
            if (column.positiveWhen) {
              const sign = column.positiveWhen(node)
              if (sign > 0) colorClass = 'num-positive'
              else if (sign < 0) colorClass = 'num-negative'
            }
            return <td key={column.key} className={`${cls} ${colorClass}`}>{content}</td>
          })}
        </tr>
        {hasChildren && isOpen ? node.children.map(child => renderNode(child)) : null}
      </>
    )
  }

  return (
    <div>
      <div className="toolbar">
        <div className="search-box">
          <span className="search-icon">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
          </span>
          <input value={search} onChange={event => setSearch(event.target.value)} placeholder={searchPlaceholder} />
        </div>
        <button type="button" className="btn-secondary" onClick={() => setExpanded(collectIds(ordered))}>Expandir Tudo</button>
        <button type="button" className="btn-secondary" onClick={() => setExpanded(new Set())}>Recolher Tudo</button>
        {infoText ? <div className="tree-info">{searching ? `${matched} itens encontrados • ` : ''}{infoText}</div> : null}
      </div>
      <div className="table-wrapper" style={{ maxHeight }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Estrutura WBS & Descrição</th>
              {columns.map(column => (
                <th key={column.key} className={column.align === 'center' ? 'text-center' : column.align === 'left' ? '' : 'text-right'}>{column.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length ? visible.map(node => renderNode(node)) : (
              <tr><td colSpan={columns.length + 1}><div className="empty-state">{emptyMessage}</div></td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export const COLS_CUSTO: TreeColumn[] = [
  { key: 'orcado', label: 'Custo Orçado', kind: 'money' },
  { key: 'realizado', label: 'Realizado', kind: 'money' },
  { key: 'estoque', label: 'Valor Estoque', kind: 'money' },
  { key: 'saldo', label: 'Saldo CT/Pedido', kind: 'money' },
  { key: 'comprometido', label: 'Comprometido', kind: 'money' },
  { key: 'saldoOrcamento', label: 'Saldo Orçamento', kind: 'money', positiveWhen: node => Number(node.saldoOrcamento || 0) },
  { key: 'medido', label: '% Medido', align: 'center', kind: 'pct' },
]

export const COLS_TENDENCIA: TreeColumn[] = [
  { key: 'orcado', label: 'Custo Orçado', kind: 'money' },
  { key: 'realizado', label: 'Realizado', kind: 'money' },
  { key: 'estoque', label: 'Valor Estoque', kind: 'money' },
  { key: 'saldo', label: 'Saldo CT/Pedido', kind: 'money' },
  { key: 'comprometido', label: 'Comprometido', kind: 'money' },
  { key: 'aComprometer', label: 'A Comprometer', kind: 'money' },
  { key: 'tendencia', label: 'Tendência', kind: 'money' },
  { key: 'resultado', label: 'Resultado', kind: 'money', positiveWhen: node => Number(node.resultado || 0) },
  { key: 'pctResultado', label: '% Resultado', align: 'center', kind: 'signed-pct' },
  { key: 'saldoOrcamento', label: 'Saldo Orçamento', kind: 'money', positiveWhen: node => Number(node.saldoOrcamento || 0) },
  { key: 'medido', label: '% Medido', align: 'center', kind: 'pct' },
]
